import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  listTemplateStylePreferences,
  updateTemplateStylePreferences,
} from '../../store/actions/memory';
import { Typography, Select, Empty } from 'antd';
import {
  DeleteOutlined,
  SaveOutlined,
  CloseOutlined,
  QuestionCircleOutlined,
} from '@ant-design/icons';
import moment from 'moment';
import TemplateSelectDropdown from '../Settings/Settings.TemplateDropdown';
import {
  listTemplates,
  listTemplateOverrides,
} from '../../store/actions/settings';
import { useAuth, useFeatureEntitlements } from '@frontegg/react';
import { showToastSuccess } from '../../../../utilities/toast';
import { ModalComponent } from '../baseComponents';
import { AlertIcon } from '../../../../icons';
import { SupaDemo } from '../../../../components/groupComponents/supaDemo';
import { analytics } from 'src/services';

const { Title, Text } = Typography;

const TemplateSelector = ({
  selectedTemplate,
  setSelectedTemplate,
  isDisabled,
  verbosityFilter,
  setVerbosityFilter,
}) => {
  const { user } = useAuth();

  return (
    <div className="mb-6 items-center gap-4">
      <div className="w-full flex mb-4">
        <label
          htmlFor="template-select"
          className="text-black text-[18px] flex items-center"
        >
          Select note template{' '}
          <SupaDemo>
            <span
              data-supademo-demo="clz07qg9i2ibdz9kd5sj5nz70"
              className="ml-4 cursor-pointer ih-button-ghost gap-2 text-[#5a8dda] underline"
              onClick={() => {
                analytics.track('Clicked Custom Template Guided Tour ', {
                  email: user?.email,
                });
              }}
            >
              <QuestionCircleOutlined /> Guided Tour
            </span>
          </SupaDemo>
        </label>
      </div>
      <div className="w-full flex items-center gap-4">
        <div className="lg:w-1/2 w-full">
          <TemplateSelectDropdown
            restrictTemplates={isDisabled}
            allowInlineEdit={false}
            encounterPhase="in-visit"
            currentTemplate={selectedTemplate}
            setCurrentTemplate={setSelectedTemplate}
            setNextWorkingTemplate={setSelectedTemplate}
            fromIsSettings={false}
          />
        </div>
        <div className="lg:w-1/4 w-full">
          <VerbosityFilter
            verbosityFilter={verbosityFilter}
            setVerbosityFilter={setVerbosityFilter}
          />
        </div>
      </div>
    </div>
  );
};

const ObservationCard = ({
  observation,
  isEditing,
  onSave,
  onCancel,
  onDelete,
  section,
  index,
  isDisabled,
}) => (
  <div className="flex justify-between items-center px-4 py-3 border border-[#e5e8eb] rounded-lg bg-white hover:shadow-sm transition-shadow">
    {isEditing ? (
      <>
        <input
          type="text"
          className="flex-1 mr-3 px-2 py-1 border border-[#e5e8eb] rounded font-sans text-sm focus:outline-none focus:border-[#00d090]"
          defaultValue={observation.description}
          data-section={section}
          data-index={index}
          disabled={isDisabled}
          autoFocus
        />
        <div className="flex gap-3 shrink-0">
          <SaveOutlined
            className={`text-base ${isDisabled ? 'text-gray-300 cursor-not-allowed' : 'text-[#00d090] cursor-pointer hover:opacity-70'}`}
            onClick={!isDisabled ? onSave : undefined}
          />
          <CloseOutlined
            className={`text-base ${isDisabled ? 'text-gray-300 cursor-not-allowed' : 'text-gray-400 cursor-pointer hover:text-gray-600'}`}
            onClick={!isDisabled ? onCancel : undefined}
          />
        </div>
      </>
    ) : (
      <>
        <Text className="text-sm text-gray-700 flex-1">
          {observation.description}
        </Text>
        <div className="shrink-0 ml-3">
          <DeleteOutlined
            className={`text-base ${isDisabled ? 'text-gray-300 cursor-not-allowed' : 'text-red-400 cursor-pointer hover:text-red-600'}`}
            onClick={!isDisabled ? onDelete : undefined}
          />
        </div>
      </>
    )}
  </div>
);

const DeleteConfirmationModal = ({ visible, onConfirm, onCancel }) => (
  <ModalComponent
    centered={true}
    open={visible}
    handleSubmit={onConfirm}
    handleCancel={onCancel}
    submitButtonText="Delete"
    cancelButtonText="Cancel"
    closable={false}
    className="!w-fit"
    additionalBody={
      <>
        <div className="text-black font-sans font-medium text-lg flex justify-center items-center gap-2">
          <span>
            <AlertIcon />
          </span>
          <p>Are you sure you want to delete this observation?</p>
        </div>
      </>
    }
  />
);

const VerbosityFilter = ({ verbosityFilter, setVerbosityFilter }) => {
  const { isEntitled: isMemoryEntitled } = useFeatureEntitlements('memory');
  return (
    <div>
      <Select
        value={verbosityFilter}
        onChange={setVerbosityFilter}
        className="w-full note-template"
        options={[
          { value: 'comprehensive', label: 'Comprehensive' },
          { value: 'balanced', label: 'Balanced' },
          { value: 'concise', label: 'Concise' },
        ]}
        disabled={!isMemoryEntitled}
      />
    </div>
  );
};

const TemplateMemory = () => {
  const dispatch = useDispatch();
  const [selectedPreference, setSelectedPreference] = useState(0);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const { templateStylePreferences } = useSelector(
    (state) => state.memoryState
  );
  const [verbosityFilter, setVerbosityFilter] = useState('comprehensive');
  const [editingState, setEditingState] = useState({
    section: null,
    index: null,
  });
  const [deleteModal, setDeleteModal] = useState({
    visible: false,
    section: null,
    index: null,
  });
  const [localPreferences, setLocalPreferences] = useState(null);
  const { user } = useAuth();
  const { currentPractitionerSettings } = useSelector((state) => ({
    ...state?.practitionerState,
  }));
  const isMemoryDisabled =
    !currentPractitionerSettings?.note_preferences?.memory;
  const { isEntitled: isMemoryEntitled } = useFeatureEntitlements('memory');

  useEffect(() => {
    dispatch(listTemplates());
    dispatch(listTemplateOverrides());
    dispatch(listTemplateStylePreferences());
    analytics.track('Viewed Memory Prefererencess', { email: user?.email });
  }, [dispatch]);

  useEffect(() => {
    if (selectedTemplate) {
      const preferenceIndex = templateStylePreferences?.findIndex(
        (pref) =>
          (pref.template_id === selectedTemplate.template_id ||
            pref.template_overrides_id ===
              selectedTemplate.template_overrides_id) &&
          pref.verbosity === verbosityFilter
      );
      setSelectedPreference(preferenceIndex);
    }
  }, [selectedTemplate, templateStylePreferences, verbosityFilter]);

  useEffect(() => {
    analytics.track('Changed Template In Memory Preferences', {
      email: user?.email,
    });
    if (templateStylePreferences?.[selectedPreference]) {
      setLocalPreferences(
        templateStylePreferences[selectedPreference].preferences
      );
    } else {
      setLocalPreferences(null);
    }
  }, [templateStylePreferences, selectedPreference]);

  const formatLastUpdated = (timestamp) => {
    if (!timestamp) return 'Never';
    return moment(timestamp).format('MM/DD/YYYY, h:mm:ss A');
  };

  const handleSave = (section, index) => {
    analytics.track('Clicked On Save In Memory Prefererences', {
      email: user?.email,
    });
    const newDescription = document.querySelector(
      `input[data-section="${section}"][data-index="${index}"]`
    ).value;

    const updatedPreferences = {
      ...localPreferences,
      [section]: [
        ...localPreferences[section].slice(0, index),
        {
          ...localPreferences[section][index],
          description: newDescription,
        },
        ...localPreferences[section].slice(index + 1),
      ],
    };

    setLocalPreferences(updatedPreferences);
    dispatch(
      updateTemplateStylePreferences(
        templateStylePreferences[selectedPreference].id,
        updatedPreferences
      )
    );
    setEditingState({ section: null, index: null });
  };

  const handleDelete = (section, observationIndex) => {
    analytics.track('Clicked On Delete In Delete Memory Prefererences', {
      email: user?.email,
    });
    setDeleteModal({
      visible: true,
      section,
      index: observationIndex,
    });
  };

  const confirmDelete = () => {
    analytics.track(
      'Clicked On Confirm Delete In Delete Memory Prefererences',
      { email: user?.email }
    );
    const updatedPreferences = {
      ...localPreferences,
      [deleteModal.section]: localPreferences[deleteModal.section].filter(
        (_, index) => index !== deleteModal.index
      ),
    };

    setLocalPreferences(updatedPreferences);
    dispatch(
      updateTemplateStylePreferences(
        templateStylePreferences[selectedPreference].id,
        updatedPreferences,
        () => {
          showToastSuccess('Successfully deleted observation');
          setDeleteModal({ visible: false, section: null, index: null });
        }
      )
    );
  };

  return (
    <div>
      <Title level={4} className="m-0 mr-2">
        Template Preferences
      </Title>

      <Text type="secondary" className="mb-6 block">
        Autogenerated based on differences between AI generated and edited
        summaries for each template.
      </Text>
      <TemplateSelector
        selectedTemplate={selectedTemplate}
        setSelectedTemplate={setSelectedTemplate}
        lastModified={
          templateStylePreferences?.[selectedPreference]?.last_modified
        }
        isDisabled={!isMemoryEntitled}
        verbosityFilter={verbosityFilter}
        setVerbosityFilter={setVerbosityFilter}
      />
      <div className="text-black font-bold text-[18px] mb-4">Sections</div>
      {!isMemoryEntitled && (
        <Text type="secondary">Upgrade to view your memory preferences.</Text>
      )}
      {isMemoryEntitled && !localPreferences && (
        <Empty description="No preferences found yet." />
      )}
      {isMemoryEntitled &&
        localPreferences &&
        Object.entries(localPreferences)
          .filter(([_, observations]) => observations.length > 0)
          .map(([section, observations]) => (
            <div
              key={section}
              className="mb-4 p-4 border rounded-lg bg-gray-50"
            >
              <div className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-3">
                {section}
              </div>
              <div className="space-y-2">
                {observations.map((observation, index) => (
                  <ObservationCard
                    key={index}
                    observation={observation}
                    isEditing={
                      editingState.section === section &&
                      editingState.index === index
                    }
                    onSave={() => handleSave(section, index)}
                    onCancel={() =>
                      setEditingState({ section: null, index: null })
                    }
                    onDelete={() => handleDelete(section, index)}
                    section={section}
                    index={index}
                    isDisabled={isMemoryDisabled}
                  />
                ))}
              </div>
            </div>
          ))}
      <div className="flex flex-col justify-end items-end">
        <Typography.Text type="secondary" className="text-xs">
          Last Updated at{' '}
          {formatLastUpdated(
            templateStylePreferences?.[selectedPreference]?.last_modified
          )}
        </Typography.Text>
      </div>
      <DeleteConfirmationModal
        visible={deleteModal.visible}
        onConfirm={confirmDelete}
        onCancel={() =>
          setDeleteModal({ visible: false, section: null, index: null })
        }
      />
    </div>
  );
};

export default TemplateMemory;
